import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Per-route document metadata.
 *
 * Updates <title>, the description, canonical link and the Open Graph /
 * Twitter tags whenever the route changes, so tabs, bookmarks, history entries
 * and link previews all say something specific rather than repeating the
 * home page's copy.
 *
 * Caveat worth knowing: the app uses HashRouter, so every route is a "#/..."
 * fragment. Crawlers that don't execute JS only ever see the static tags in
 * public/index.html, and fragments aren't indexed as separate URLs either way.
 * Switching to BrowserRouter (vercel.json already rewrites to index.html)
 * is what would make these routes independently indexable.
 */

const SITE = "https://shaurya.vercel.app";
const NAME = "Shaurya Singhal";

interface Meta { title: string; description: string; }

const ROUTES: Record<string, Meta> = {
  "/": {
    title: `${NAME} — Member of Technical Staff 2 @ Adobe | Portfolio`,
    description:
      `${NAME} — Member of Technical Staff 2 at Adobe (Document Cloud), previously Senior Software Engineer at Infoedge (Naukri Campus). LeetCode Guardian, GATE'21 AIR 2547, open source contributor.`,
  },
  "/skills": {
    title: `Skills — React, TypeScript, Java, System Design | ${NAME}`,
    description:
      `The tools ${NAME} builds with: React, TypeScript, React Native, Node.js, Java, Spring Boot, Kafka, SQL and PostgreSQL, sorted into Hogwarts houses.`,
  },
  "/experience": {
    title: `Experience — Adobe, Infoedge (Naukri Campus) | ${NAME}`,
    description:
      `Career of ${NAME}: Member of Technical Staff 2 at Adobe, three years at Infoedge (Naukri Campus) impacting 200K+ users, plus open source at React Native Elements and Coding Blocks.`,
  },
  "/leetcode": {
    title: `LeetCode — Guardian, 2244 Rating, Top 0.8% | ${NAME}`,
    description:
      `Competitive programming record of ${NAME}: LeetCode Guardian at 2244, top 0.8% worldwide, 771+ problems solved and 36 contests, with a best rank of 262.`,
  },
  "/projects": {
    title: `Projects — Naukri Campus & Open Source Work | ${NAME}`,
    description:
      `Products ${NAME} has shipped, from the Naukri Campus ambassador programme and contest platforms to Productive Weekday and other open source work.`,
  },
  "/education": {
    title: `Education — MCA, B.Sc CS, GATE'21 AIR 2547 | ${NAME}`,
    description:
      `Academic record of ${NAME}: MCA and B.Sc (Hons.) Computer Science from the University of Delhi, GATE 2021 AIR 2547 and DU Entrance AIR 13.`,
  },
  "/opensource": {
    title: `Open Source — 35+ Merged PRs, BOSS'20 Rank 6 | ${NAME}`,
    description:
      `Open source contributions by ${NAME}: 35+ merged pull requests across React Native Elements and Coding Blocks, and 6th place in Bountiful Open Source Summer 2020.`,
  },
  "/contact": {
    title: `Contact — Send an Owl | ${NAME}`,
    description:
      `Get in touch with ${NAME} — open to collaboration, opportunities and competitive programming. Email, LinkedIn, GitHub and Twitter.`,
  },
};

const NOT_FOUND: Meta = {
  title: `Page not found | ${NAME}`,
  description: `That page doesn't exist. Find your way back to ${NAME}'s portfolio.`,
};

/** Set a <meta> by name or property, creating it if the page lacks one. */
function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

const PageMeta: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = ROUTES[pathname] || NOT_FOUND;
    const url = pathname === "/" ? `${SITE}/` : `${SITE}/#${pathname}`;

    document.title = meta.title;
    setMeta("name", "description", meta.description);

    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("property", "og:url", url);

    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.description);
    setMeta("name", "twitter:url", url);

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = url;
  }, [pathname]);

  return null;
};

export default PageMeta;
